import { useQuery } from "@tanstack/react-query"

import { filterPublicLandingPlans } from "@/lib/plan-marketing-copy"
import { isLaunchMode } from "@/lib/launch-config"
import { listPricingPlanRows } from "@/lib/marketing-rpc"
import { parsePublicPlanRow, type PublicSubscriptionPlan } from "@/lib/subscription-types"

const MOCK_MODE = import.meta.env.VITE_USE_MOCK_DATA === "true"

const MOCK_PLANS: PublicSubscriptionPlan[] = [
  {
    id: "builder",
    name: "Builder",
    description: "For individuals and small teams writing behavioral scenarios directly in the app",
    priceCents: 0,
    priceInterval: "month",
    seatPriceCents: null,
    limits: {
      included_seats: 3,
      max_projects: 2,
      max_repo_connections: 2,
      max_scenarios: 300,
      sync_mode: "manual",
      live_webhook_sync: false,
      advanced_editor: false,
      ai_copilot: false,
      intelligent_search: false,
      mcp_server: true,
      status_automation: false,
      custom_roles: false,
      public_api: false,
    },
    marketingFeatures: [
      "Admin and Member roles per project",
      "3 seats",
      "Up to 2 projects",
      "Up to 300 scenarios",
      "MCP server access for AI coding agents",
      "Explore, scenarios, and features views",
      "Lifecycle and verification status tracking",
      "Quick scenario edits (title and I/O)",
    ],
    sortOrder: 0,
  },
  {
    id: "pro",
    name: "Pro",
    description: "For teams that need higher limits, granular per-project roles, and room to grow",
    priceCents: null,
    priceInterval: "month",
    seatPriceCents: 400,
    limits: {
      included_seats: null,
      max_projects: null,
      max_repo_connections: null,
      max_scenarios: null,
      sync_mode: "live",
      live_webhook_sync: true,
      advanced_editor: true,
      ai_copilot: true,
      intelligent_search: true,
      mcp_server: true,
      status_automation: true,
      custom_roles: true,
      public_api: true,
    },
    marketingFeatures: [
      "Per-project roles: Reader, Reviewer, Editor, Admin",
      "$4 per seat, per month",
      "Unlimited projects",
      "Unlimited scenarios",
      "MCP server access for AI coding agents",
    ],
    sortOrder: 1,
  },
]

/** Static catalog for coming-soon marketing when Supabase is unavailable or not yet migrated. */
export function getPublicLandingMockPlans(): PublicSubscriptionPlan[] {
  return filterPublicLandingPlans(MOCK_PLANS)
}

export function usePublicSubscriptionPlans() {
  return useQuery({
    queryKey: ["public-subscription-plans"],
    queryFn: async (): Promise<PublicSubscriptionPlan[]> => {
      if (MOCK_MODE) return MOCK_PLANS
      try {
        return (await listPricingPlanRows()).map((row) => parsePublicPlanRow(row))
      } catch (error) {
        if (isLaunchMode()) return MOCK_PLANS
        throw error
      }
    },
    placeholderData: isLaunchMode() ? () => MOCK_PLANS : undefined,
    staleTime: 60_000,
  })
}
